import { useCallback, useEffect, useState } from 'react';
import {
    FiChevronDown,
    FiChevronUp,
    FiDownload,
    FiFileText,
    FiLoader,
    FiPlus,
    FiRotateCcw,
    FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ImportLancamentosModal } from '../../components/imports/ImportLancamentosModal';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { Button, confirmDialog, toast } from '../../components/ui';
import { useAuth } from '../../contexts';
import { api } from '../../services/api';
import type { ImportBatchDetails, ImportBatchSummary } from '../../types/imports';
import styles from './Importacoes.module.css';

const moeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});

/**
 * Histórico de importações de planilha da filial ativa.
 *
 * Reverter e excluir são duas ações distintas de propósito: reverter apaga os
 * lançamentos e mantém o registro, excluir só é liberado depois disso. Assim
 * não existe caminho que apague o histórico deixando lançamento na conta sem
 * rastro de onde veio.
 */
export function Importacoes() {
    const navigate = useNavigate();
    // A pagina ja esta atras da feature 'import' na rota; aqui e o perfil de
    // acesso que decide quem pode trazer planilha e quem pode desfazer.
    const { hasPermission } = useAuth();
    const podeImportar = hasPermission('IMPORTACOES', 'CREATE');
    const podeExcluir = hasPermission('IMPORTACOES', 'DELETE');

    const [lotes, setLotes] = useState<ImportBatchSummary[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [modalAberto, setModalAberto] = useState(false);
    const [detalhes, setDetalhes] = useState<Record<string, ImportBatchDetails>>({});
    const [expandido, setExpandido] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState<string | null>(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setLotes(await api.listImportBatches());
        } catch (erro) {
            toast(erro instanceof Error ? erro.message : 'Erro ao carregar as importações', 'error');
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    const alternarDetalhes = async (lote: ImportBatchSummary) => {
        if (expandido === lote.id) {
            setExpandido(null);
            return;
        }
        setExpandido(lote.id);
        if (detalhes[lote.id]) return;

        try {
            const resposta = await api.getImportBatchDetails(lote.id);
            setDetalhes((atual) => ({ ...atual, [lote.id]: resposta }));
        } catch (erro) {
            toast(erro instanceof Error ? erro.message : 'Erro ao carregar o detalhe da importação', 'error');
            setExpandido(null);
        }
    };

    const reverter = async (lote: ImportBatchSummary) => {
        const confirmado = await confirmDialog(
            `${lote.fileName}\n\n${lote.totalLancamentos} lançamento(s) desta importação serão removidos.\n\nOs grupos, categorias e formas de pagamento que esta importação criou também saem do plano de contas — menos os que já estiverem sendo usados por outros lançamentos, que ficam.`,
            'Reverter importação?',
        );
        if (!confirmado) return;

        setOcupado(lote.id);
        try {
            const resposta = await api.revertImportBatch(lote.id);
            const plano = resposta.planoDeContas;
            const saiuDoPlano = plano.categorias + plano.subCategorias + plano.formasPagamento;
            const detalhe = plano.naoRastreado
                ? ' O plano de contas ficou intacto: esta importação é anterior ao registro do que cada lote cria.'
                : saiuDoPlano > 0
                    ? ` ${saiuDoPlano} item(ns) saíram do plano de contas${plano.mantidasEmUso > 0 ? `, e ${plano.mantidasEmUso} ficaram por estarem em uso` : ''}.`
                    : plano.mantidasEmUso > 0
                        ? ` Nada saiu do plano de contas: ${plano.mantidasEmUso} item(ns) continuam em uso.`
                        : '';
            toast(`${resposta.deleted} lançamento(s) removido(s).${detalhe}`, 'success');
            setDetalhes((atual) => {
                const proxima = { ...atual };
                delete proxima[lote.id];
                return proxima;
            });
            await carregar();
        } catch (erro) {
            toast(erro instanceof Error ? erro.message : 'Erro ao reverter a importação', 'error');
        } finally {
            setOcupado(null);
        }
    };

    const excluir = async (lote: ImportBatchSummary) => {
        const confirmado = await confirmDialog(
            `${lote.fileName}\n\nO registro desta importação sai da lista. Os lançamentos já foram removidos na reversão e não voltam.`,
            'Excluir do histórico?',
        );
        if (!confirmado) return;

        setOcupado(lote.id);
        try {
            await api.destroyImportBatch(lote.id);
            toast('Importação excluída do histórico', 'success');
            await carregar();
        } catch (erro) {
            toast(erro instanceof Error ? erro.message : 'Erro ao excluir a importação', 'error');
        } finally {
            setOcupado(null);
        }
    };

    const baixar = async (lote: ImportBatchSummary, formato: 'xlsx' | 'pdf') => {
        const base = lote.fileName.replace(/\.(xlsx|xlsm|csv)$/i, '');
        try {
            if (formato === 'xlsx') await api.downloadImportBatchXlsx(lote.id, base);
            else await api.downloadImportBatchPdf(lote.id, base);
        } catch (erro) {
            toast(erro instanceof Error ? erro.message : 'Erro ao baixar o arquivo', 'error');
        }
    };

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title="Importações de planilha" subtitle="Veja importações anteriores ou traga uma nova planilha." />

                <div className={styles.content}>
                    <div className={styles.toolbar}>
                        {podeImportar && (
                            <Button type="button" onClick={() => setModalAberto(true)}>
                                <FiPlus size={16} /> Nova importação
                            </Button>
                        )}
                        <Button type="button" variant="outline" onClick={() => navigate('/lancamentos')}>
                            Ver lançamentos
                        </Button>
                    </div>

                    {carregando ? (
                        <div className={styles.loading}>
                            <FiLoader className="animate-spin" size={28} />
                            <span>Carregando...</span>
                        </div>
                    ) : lotes.length === 0 ? (
                        <div className={styles.vazio}>
                            <FiFileText size={28} />
                            <strong>Nenhuma importação por aqui ainda</strong>
                            <span>Quando você importar uma planilha, ela aparece nesta lista com os totais e a opção de reverter.</span>
                        </div>
                    ) : (
                        lotes.map((lote) => {
                            const detalhe = detalhes[lote.id];
                            const aberto = expandido === lote.id;

                            return (
                                <article key={lote.id} className={styles.card}>
                                    <div className={styles.cardTopo}>
                                        <div className={styles.cardIdentidade}>
                                            <div className={styles.cardTitulo}>
                                                <strong>{lote.fileName}</strong>
                                                <span className={lote.revertido ? styles.badgeRevertido : styles.badgeImportado}>
                                                    {lote.revertido ? 'Revertido' : 'Importado'}
                                                </span>
                                            </div>
                                            <span className={styles.cardData}>{dataHora(lote.confirmadoEm ?? lote.createdAt)}</span>
                                        </div>

                                        <div className={styles.cardAcoes}>
                                            <Button type="button" variant="outline" onClick={() => alternarDetalhes(lote)}>
                                                {aberto ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />} Detalhes
                                            </Button>
                                            {!lote.revertido && podeExcluir && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => reverter(lote)}
                                                    isLoading={ocupado === lote.id}
                                                >
                                                    <FiRotateCcw size={15} /> Reverter
                                                </Button>
                                            )}
                                            {lote.revertido && podeExcluir && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => excluir(lote)}
                                                    isLoading={ocupado === lote.id}
                                                >
                                                    <FiTrash2 size={15} /> Excluir
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.metricas}>
                                        <div>
                                            <span className={styles.metricaLabel}>Entradas ({lote.entradasCount})</span>
                                            <strong className={styles.metricaEntrada}>{moeda(lote.entradasValor)}</strong>
                                        </div>
                                        <div>
                                            <span className={styles.metricaLabel}>Saídas ({lote.saidasCount})</span>
                                            <strong className={styles.metricaSaida}>{moeda(lote.saidasValor)}</strong>
                                        </div>
                                        <div>
                                            <span className={styles.metricaLabel}>Total movimentado</span>
                                            <strong>{moeda(lote.totalMovimentado)}</strong>
                                        </div>
                                        <div>
                                            <span className={styles.metricaLabel}>Lançamentos</span>
                                            <strong>{lote.totalLancamentos}</strong>
                                        </div>
                                    </div>

                                    {aberto && (
                                        <div className={styles.detalhe}>
                                            {!detalhe ? (
                                                <div className={styles.loading}>
                                                    <FiLoader className="animate-spin" size={20} />
                                                    <span>Carregando lançamentos...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.detalheAcoes}>
                                                        <Button type="button" variant="outline" onClick={() => baixar(lote, 'xlsx')}>
                                                            <FiDownload size={15} /> Excel
                                                        </Button>
                                                        <Button type="button" variant="outline" onClick={() => baixar(lote, 'pdf')}>
                                                            <FiDownload size={15} /> PDF
                                                        </Button>
                                                        {lote.revertido && (
                                                            <span className={styles.avisoRevertido}>
                                                                Importação revertida: a lista abaixo é o registro do que foi importado na época.
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className={styles.tabelaWrap}>
                                                        <table className={styles.tabela}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Data</th>
                                                                    <th>Tipo</th>
                                                                    <th>Categoria</th>
                                                                    <th>Forma de pagamento</th>
                                                                    <th className={styles.colValor}>Valor</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {detalhe.lancamentos.map((lancamento, indice) => (
                                                                    <tr key={lancamento.id ?? `linha-${indice}`}>
                                                                        <td>{lancamento.data ? new Date(lancamento.data).toLocaleDateString('pt-BR') : '-'}</td>
                                                                        <td className={lancamento.categoria === 'RECEITA' ? styles.tipoEntrada : styles.tipoSaida}>
                                                                            {lancamento.categoria === 'RECEITA' ? 'Receita' : 'Despesa'}
                                                                        </td>
                                                                        <td>{lancamento.subCategoria || '-'}</td>
                                                                        <td>{lancamento.formaPagamento || '-'}</td>
                                                                        <td className={styles.colValor}>{moeda(lancamento.valor)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {detalhe.discardedRows.length > 0 && (
                                                        <p className={styles.descartadas}>
                                                            {detalhe.discardedRows.length} linha(s) da planilha foram descartadas nesta importação.
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </article>
                            );
                        })
                    )}
                </div>
            </main>

            {modalAberto && (
                <ImportLancamentosModal
                    onClose={() => {
                        setModalAberto(false);
                        void carregar();
                    }}
                />
            )}
        </div>
    );
}
